import React, { useEffect, useState, useCallback } from 'react';
import { Grid, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';

import EditableTable from '../elements/EditableTable';
import AddForm from './AddForm';
import EntityDeleteConfirmation from './EntityDeleteConfirmation';
import AutomationCredentials from '../automation/AutomationCredentials';
import AutomationIntegration from '../automation/AutomationIntegration';
import ExecutionScope from './config/ExecutionScope';
import Integrations from '../integrations';
import ProjectAIConfiguration from '../ai-config/ProjectAIConfiguration';
import {
  getConfigurations,
  addEntity,
  deleteEntity,
  updateEntity,
} from '../../modules/config';
import { getConfigDetails, getTableHeader } from './tableData';
import { useNotification } from '../../hooks/NotificationContext';
import CustomFieldsComponent from './CustomFieldsComponent';
import NotificationSettings from '../MyProfile/notifications/notificationSettings';
import { useAccess } from '../../hooks/useAccess';

const Configurations = ({ selectedTab, user }) => {
  const { projectId } = useParams();
  const { heading, group } = getConfigDetails(selectedTab);
  const { addNotification } = useNotification();
  const { canAccess } = useAccess();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState(null);

  const isAdmin = canAccess('ManageProjectConfig');
  const isJiraAdmin = canAccess('ManageJiraConfig');
  const isNotificationAdmin = canAccess('ManageProjectNotifications');
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getConfigurations(selectedTab, projectId);
      if (response) {
        setData(response.data);
      }
    } catch (error) {
      addNotification('danger', error.message);
    } finally {
      setLoading(false);
    }
  }, [selectedTab, projectId, addNotification]);

  useEffect(() => {
    if (!['api-token', 'integration', 'scope', 'jira', 'automationfields', 'notifications', 'ai-config'].includes(selectedTab)) {
      fetchData(); 
    }
  }, [selectedTab, fetchData]);

  const handleUpdateEntity = async (id, editingValues) => {
    try {
      await updateEntity(id, { entity: editingValues });
      addNotification('success', 'Updated successfully');
      fetchData();
    } catch (error) {
      addNotification('danger', error.message);
    }
  };

  const handleDeleteClick = (id) => {
    setEntityToDelete(id);
    setShowConfirmationModal(true);
  };

  const handleConfirmDelete = async () => {
    setLoadingDelete(true);
    try {
      await deleteEntity(entityToDelete);
      addNotification('success', 'Deleted successfully');
      fetchData();
    } catch (error) {
      addNotification('danger', error.message);
    } finally {
      setLoadingDelete(false);
      handleCloseModal();
    }
  };

  const handleAddNewEntity = async (formData) => {
    setLoading(true);
    try {
      await addEntity({ entity: { ...formData, group } }, projectId);
      addNotification('success', 'Added successfully');
      fetchData();
    } catch (error) {
      addNotification(
        'danger',
        error.message || 'Failed to create. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowConfirmationModal(false);
    setEntityToDelete(null);
  };

  const renderTabContent = () => {
    switch (selectedTab) {
      case 'api-token':
      case 'integration':
        return selectedTab === 'api-token' ? (
          <Grid item xs={12}>
            <AutomationCredentials selectedTab={selectedTab} />
          </Grid>
        ) : (
          <Grid item xs={12}>
            <AutomationIntegration isAdmin={isAdmin} />
          </Grid>
        );
      case 'scope':
        return (
          <Grid item xs={12}>
            <ExecutionScope isAdmin={isAdmin} />{' '}
          </Grid>
        );
      case 'jira':
        return (
          <Grid item xs={12}>
            <Integrations isAdmin={isJiraAdmin} />
          </Grid>
        );
      case 'automationfields':
        return (
          <Grid item xs={12}>
            <CustomFieldsComponent isAdmin={isAdmin} selectedTab={selectedTab} />
          </Grid>
        );

      case 'ai-config':
        return (
          <Grid item xs={12}>
         <ProjectAIConfiguration user={user} projectId={projectId} />
          </Grid>
        );

      case 'notifications':
        return (
          <Grid item xs={12}>
            <NotificationSettings isAdmin={isNotificationAdmin} user={user} />
          </Grid>
        );
      default:
        return (
          <>
            {isAdmin && (
              <Grid item xs={12}>
                <AddForm
                  entityType={selectedTab}
                  group={group}
                  handleSubmit={handleAddNewEntity}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <EditableTable
                data={data}
                type={selectedTab}
                headerTabs={getTableHeader(selectedTab)}
                order="asc"
                isAdmin={isAdmin}
                isLoading={loading || loadingDelete}
                setLoading={setLoading}
                handleUpdateEntity={handleUpdateEntity}
                fetchData={fetchData}
                editableFields={['name', 'description']}
                canEdit={(row) => row.gainss_project_id !== null && row.scope !== 'GLOBAL'}
                handleDeleteClick={handleDeleteClick}
                canDelete={(row) => row.scope !== 'GLOBAL'}
              />
              <EntityDeleteConfirmation
                show={showConfirmationModal}
                handleClose={handleCloseModal}
                handleConfirm={handleConfirmDelete}
                entityToDelete={entityToDelete}
              />
            </Grid>
          </>
        );
    }
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Typography variant="h4" className="mb-2">
          {heading || 'Configurations'}
        </Typography>
      </Grid>
      {renderTabContent()}
    </Grid>
  );
};

export default Configurations;
